import './collage-focus.scss';
import { gsap } from 'gsap';
import { CustomEase } from 'gsap/CustomEase';

// Osmo — Collage Focus Card on Hover, overgenomen. Afwijkend van Osmo:
//   1. minPushPercent: de buren schuiven altijd op. Osmo duwt alleen weg wat de focus
//      raakt; dat werkt in de dichte demo, maar hier liggen twee groepen foto's los
//      van elkaar en bewoog er bij sommige foto's dan vrijwel niets.
//   2. raiseDelay: de foto's overlappen hier meer, dus de focus komt bovenop. Dat
//      gebeurt net na de start, als de buren al wijken, en zakt pas als de foto weer
//      op z'n plek ligt. Zo verspringt er niets.
//   3. prefers-reduced-motion slaat de animatie over.
gsap.registerPlugin(CustomEase);
CustomEase.create("move", "0.3, 0.075, 0, 1");

function initCollageFocusCardOnHover() {
  const activeScale = 1.075;
  const inactiveScale = 0.9;
  const gapPercent = 3;
  const minPushPercent = 15;
  const raiseDelay = 0.15;
  const secondCardBoost = 1.35;
  const centerPullPercent = 25;
  const duration = 0.8;
  const ease = "move";

  document.querySelectorAll('[data-interactive-collage-init]').forEach(root => {
    root._interactiveCollageAbort?.abort();

    const list = root.querySelector('[data-interactive-collage-list]');
    const items = [...root.querySelectorAll('[data-interactive-collage-item]')];
    const isTouch = !window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!list || !items.length) return;

    const controller = new AbortController();
    const { signal } = controller;
    let activeItem = null;
    let topZIndex = 10;
    const getInner = item => item.querySelector('[data-interactive-collage-item-inner]');

    const getMoveStrength = distance => {
      const strength = 1 / (1 + Math.pow(distance - 1, 1.2) * 0.45);
      return distance === 2 ? strength * secondCardBoost : strength;
    };

    const animateItem = (item, xPercent, yPercent, scale) => {
      gsap.to(getInner(item), {
        xPercent,
        yPercent,
        scale,
        duration,
        ease,
        overwrite: true
      });
    };

    const lowerItem = item => {
      if (item) gsap.set(item, { clearProps: 'zIndex', delay: duration, overwrite: true });
    };

    const resetCollage = () => {
      lowerItem(activeItem);
      activeItem = null;

      items.forEach(item => {
        item.removeAttribute('data-interactive-collage-focus');
        animateItem(item, 0, 0, 1);
      });
    };

    const focusItem = active => {
      if (activeItem === active) return;

      lowerItem(activeItem);
      topZIndex += 1;
      gsap.set(active, { zIndex: topZIndex, delay: raiseDelay, overwrite: true });

      activeItem = active;

      items.forEach(item => {
        if (item === active) {
          item.setAttribute('data-interactive-collage-focus', '');
        } else {
          item.removeAttribute('data-interactive-collage-focus');
        }
      });

      const listRect = list.getBoundingClientRect();
      const listCenterY = listRect.top + listRect.height / 2;
      const gap = listRect.width * gapPercent / 100;
      const minPush = listRect.width * minPushPercent / 100;

      const orderedItems = [...items].sort((a, b) => {
        const aRect = a.getBoundingClientRect();
        const bRect = b.getBoundingClientRect();

        return aRect.left + aRect.width / 2 - (bRect.left + bRect.width / 2);
      });

      const activeIndex = orderedItems.indexOf(active);
      const activeRect = active.getBoundingClientRect();
      const activeCenterX = activeRect.left + activeRect.width / 2;
      const activeLeft = activeCenterX - activeRect.width * activeScale / 2;
      const activeRight = activeCenterX + activeRect.width * activeScale / 2;

      const leftItem = orderedItems[activeIndex - 1];
      const rightItem = orderedItems[activeIndex + 1];

      let leftMove = 0;
      let rightMove = 0;

      if (leftItem) {
        const rect = leftItem.getBoundingClientRect();
        const itemRight = rect.left + rect.width / 2 + rect.width * inactiveScale / 2;

        leftMove = Math.min(-minPush, activeLeft - gap - itemRight);
      }

      if (rightItem) {
        const rect = rightItem.getBoundingClientRect();
        const itemLeft = rect.left + rect.width / 2 - rect.width * inactiveScale / 2;

        rightMove = Math.max(minPush, activeRight + gap - itemLeft);
      }

      orderedItems.forEach((item, index) => {
        if (item === active) {
          animateItem(item, 0, 0, activeScale);
          return;
        }

        const rect = item.getBoundingClientRect();
        const difference = index - activeIndex;
        const distance = Math.abs(difference);
        const strength = getMoveStrength(distance);
        const itemCenterY = rect.top + rect.height / 2;
        const centerProgress = (listCenterY - itemCenterY) / (listRect.height / 2);
        const moveX = difference < 0 ? leftMove * strength : rightMove * strength;
        const scale = inactiveScale - (1 - strength) * 0.12;

        animateItem(item, moveX / rect.width * 100, centerPullPercent * centerProgress * strength, scale);
      });
    };

    const getHoveredItem = event => {
      if (activeItem) {
        const rect = getInner(activeItem).getBoundingClientRect();

        if (
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom
        ) {
          return activeItem;
        }
      }

      return document.elementFromPoint(event.clientX, event.clientY)?.closest('[data-interactive-collage-item]') || null;
    };

    if (isTouch) {
      items.forEach(item => {
        item.addEventListener('click', event => {
          event.stopPropagation();
          activeItem === item ? resetCollage() : focusItem(item);
        }, { signal });
      });

      document.addEventListener('click', event => {
        if (!root.contains(event.target)) resetCollage();
      }, { signal });
    } else {
      root.addEventListener('pointermove', event => {
        const item = getHoveredItem(event);
        item ? focusItem(item) : resetCollage();
      }, { signal });

      root.addEventListener('pointerleave', resetCollage, { signal });
    }

    root._interactiveCollageAbort = controller;
  });
}

if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCollageFocusCardOnHover);
  } else {
    initCollageFocusCardOnHover();
  }
}
